import GuestPartialPaymentOption from './GuestPartialPaymentOption';
import '../css/BookingTotalRow.css';
import { formatMoney } from '../utils/currencyUtil';
import { getMinimumPartialAmount } from '../utils/partialPaymentUtil';

/**
 * The Total line on the guest's Booking details card, the checkout screen
 * and the admin's Reservation details. When the club accepts partial
 * payments, the "Partial payment" checkbox sits beside the Total label;
 * ticking it greys and strikes through the full total (in a smaller size) and
 * shows the partial amount in its place as the figure to pay.
 *
 * @param {object} props
 * @param {number} props.total - the booking's full amount
 * @param {object} props.config - the club config, for the currency format
 * @param {boolean} [props.showPartialOption] - whether to offer the checkbox
 *   (Config -> Pricing -> "Partial Payment Allowed (%)" above zero, total > 0)
 * @param {boolean} [props.partialChecked] - whether the guest ticked it
 * @param {(checked: boolean) => void} [props.onPartialChange]
 * @param {number} [props.partialPercent] - the configured percentage
 * @param {boolean} [props.partialLocked] - freezes the checkbox once the
 *   choice has been submitted with the receipt
 * @param {string} props.idPrefix - makes the checkbox id unique per screen
 * @param {string} [props.className] - the row's own class: 'uif-total-row'
 *   on the guest card, 'arq-total-row' in the admin modal
 * @returns {JSX.Element}
 */
function BookingTotalRow({
  total,
  config,
  showPartialOption = false,
  partialChecked = false,
  // A no-op default: the admin modal shows the guest's choice read-only.
  onPartialChange = () => {},
  partialPercent = 0,
  partialLocked = false,
  idPrefix,
  className = 'uif-total-row',
}) {
  const payingPartial = showPartialOption && partialChecked;
  // Same rounding-up rule the admin's "Confirm (Partial)" enforces, so the
  // figure shown is never below what the admin will accept.
  const partialAmount = payingPartial ? getMinimumPartialAmount(total, partialPercent) : null;

  return (
    <div className={`${className} btr-row`}>
      <div className="btr-label">
        <span className="btr-title">Total</span>
        {showPartialOption && (
          <GuestPartialPaymentOption
            id={`${idPrefix}-partial-payment`}
            checked={partialChecked}
            onChange={onPartialChange}
            percent={partialPercent}
            disabled={partialLocked}
          />
        )}
      </div>
      <div className="btr-amounts">
        {payingPartial && (
          <s className="btr-full-struck" aria-label={`Full total ${formatMoney(total, config)}`}>
            {formatMoney(total, config)}
          </s>
        )}
        <span className="uif-total-amt">{formatMoney(payingPartial ? partialAmount : total, config)}</span>
      </div>
    </div>
  );
}

export default BookingTotalRow;
